'use client';

import React from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../primitives/tabs';
import { cn } from '../../utils/cn';
import { UzBadge } from '../feedback/UzBadge';

import type { ReactNode } from 'react';

export interface UzTabDefinition<TValue extends string = string> {
  value: TValue;
  label: ReactNode;
  /** Optional count/badge after the label. */
  count?: number;
  /** Tab body content. */
  content: ReactNode;
  disabled?: boolean;
  icon?: ReactNode;
}

export interface UzTabsProps<TValue extends string = string> {
  value?: TValue;
  defaultValue?: TValue;
  onValueChange?: (value: TValue) => void;
  tabs: ReadonlyArray<UzTabDefinition<TValue>>;
  /** Visual orientation. */
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  /**
   * Extra classes for the tab strip itself. Use to contain a long strip
   * (`max-w-full overflow-x-auto`) so it scrolls on its own instead of pushing
   * the panel below it sideways.
   */
  listClassName?: string;
}

export const UzTabs = <TValue extends string = string>({
  value,
  defaultValue,
  onValueChange,
  tabs,
  orientation = 'horizontal',
  className,
  listClassName,
}: UzTabsProps<TValue>): React.JSX.Element => (
  <Tabs
    value={value}
    defaultValue={defaultValue ?? tabs[0]?.value}
    onValueChange={(v) => onValueChange?.(v as TValue)}
    orientation={orientation}
    className={cn(orientation === 'vertical' && 'flex gap-6', className)}
  >
    <TabsList className={cn(orientation === 'vertical' && 'flex h-auto flex-col', listClassName)}>
      {tabs.map((tab) => (
        <TabsTrigger key={tab.value} value={tab.value} disabled={tab.disabled} className="gap-2">
          {tab.icon}
          {tab.label}
          {typeof tab.count === 'number' && (
            <UzBadge tone="secondary" pill={false} className="px-1.5 py-0 text-[10px]">
              {tab.count}
            </UzBadge>
          )}
        </TabsTrigger>
      ))}
    </TabsList>
    <div className={cn(orientation === 'vertical' && 'flex-1')}>
      {tabs.map((tab) => (
        <TabsContent key={tab.value} value={tab.value}>
          {tab.content}
        </TabsContent>
      ))}
    </div>
  </Tabs>
);
